export const GAME_SETTINGS_KEY='turbo-crown-settings-v1';

export const GRAPHICS_PRESETS=Object.freeze({
  low:Object.freeze({renderScale:60,shadowQuality:'off',particleQuality:'low',fpsLimit:30}),
  medium:Object.freeze({renderScale:80,shadowQuality:'low',particleQuality:'medium',fpsLimit:60}),
  high:Object.freeze({renderScale:100,shadowQuality:'high',particleQuality:'high',fpsLimit:60}),
});

const DEFAULT_GRAPHICS=Object.freeze({
  preset:'auto',
  renderScale:100,
  shadowQuality:'high',
  particleQuality:'high',
  fpsLimit:60,
  showFps:false,
});

export const DEFAULT_GAME_SETTINGS=Object.freeze({
  language:'es-419',
  audio:Object.freeze({master:100,music:70,effects:100}),
  gameplay:Object.freeze({
    cameraShake:100,
    controllerVibration:true,
    controllerVibrationStrength:100,
    mouseSensitivity:42,
    invertMouseY:false,
    controllerAimSensitivity:50,
    controllerAimAssist:true,
    controllerAimAssistStrength:45,
  }),
  graphics:DEFAULT_GRAPHICS,
  controls:Object.freeze({
    forward:'KeyW',
    backward:'KeyS',
    left:'KeyA',
    right:'KeyD',
    jump:'Space',
    handbrake:'KeyX',
    boost:'ShiftLeft',
    stabilize:'KeyQ',
    powerup:'KeyE',
    cameraDistance:'KeyC',
  }),
  mouse:Object.freeze({fireButton:0,cameraReverseButton:1,cameraControlButton:2}),
});

const clampPercent=(value,fallback)=>{
  const number=Number(value);
  return Number.isFinite(number)?Math.max(0,Math.min(100,Math.round(number))):fallback;
};

const GRAPHICS_PRESET_IDS=new Set(['auto','low','medium','high','custom']);
const SHADOW_QUALITY_IDS=new Set(['off','low','high']);
const PARTICLE_QUALITY_IDS=new Set(['low','medium','high']);
const FPS_LIMITS=new Set([0,30,60,120]);

function normalizeGraphicsSettings(value={}){
  const source=value&&typeof value==='object'?value:{};
  const renderScale=Number(source.renderScale);
  const fpsLimit=Number(source.fpsLimit);
  return {
    preset:GRAPHICS_PRESET_IDS.has(source.preset)?source.preset:DEFAULT_GRAPHICS.preset,
    renderScale:Number.isFinite(renderScale)
      ?Math.max(50,Math.min(125,Math.round(renderScale/5)*5))
      :DEFAULT_GRAPHICS.renderScale,
    shadowQuality:SHADOW_QUALITY_IDS.has(source.shadowQuality)
      ?source.shadowQuality
      :DEFAULT_GRAPHICS.shadowQuality,
    particleQuality:PARTICLE_QUALITY_IDS.has(source.particleQuality)
      ?source.particleQuality
      :DEFAULT_GRAPHICS.particleQuality,
    fpsLimit:FPS_LIMITS.has(fpsLimit)?fpsLimit:DEFAULT_GRAPHICS.fpsLimit,
    showFps:source.showFps===true,
  };
}

export function detectAutomaticGraphicsPreset(environment=globalThis){
  const navigatorInfo=environment?.navigator||{};
  const memory=Number(navigatorInfo.deviceMemory)||0;
  const cores=Number(navigatorInfo.hardwareConcurrency)||0;
  if((memory>0&&memory<=4)||(cores>0&&cores<=4))return 'low';
  if((memory>0&&memory<=8)||(cores>0&&cores<=8))return 'medium';
  return 'high';
}

export function getEffectiveGraphicsSettings(value={},environment=globalThis){
  const graphics=normalizeGraphicsSettings(value);
  const resolvedPreset=graphics.preset==='auto'
    ?detectAutomaticGraphicsPreset(environment)
    :graphics.preset;
  const preset=GRAPHICS_PRESETS[resolvedPreset];
  return {
    ...(preset||graphics),
    preset:graphics.preset,
    resolvedPreset:preset?resolvedPreset:'custom',
    showFps:graphics.showFps,
  };
}

export function normalizeGameSettings(value={}){
  const source=value&&typeof value==='object'?value:{};
  const controls=source.controls&&typeof source.controls==='object'?source.controls:{};
  return {
    language:source.language==='en'?'en':'es-419',
    audio:{
      master:clampPercent(source.audio?.master,DEFAULT_GAME_SETTINGS.audio.master),
      music:clampPercent(source.audio?.music,DEFAULT_GAME_SETTINGS.audio.music),
      effects:clampPercent(source.audio?.effects,DEFAULT_GAME_SETTINGS.audio.effects),
    },
    gameplay:{
      cameraShake:clampPercent(source.gameplay?.cameraShake,DEFAULT_GAME_SETTINGS.gameplay.cameraShake),
      controllerVibration:source.gameplay?.controllerVibration!==false,
      controllerVibrationStrength:clampPercent(
        source.gameplay?.controllerVibrationStrength,
        DEFAULT_GAME_SETTINGS.gameplay.controllerVibrationStrength,
      ),
      mouseSensitivity:clampPercent(source.gameplay?.mouseSensitivity,DEFAULT_GAME_SETTINGS.gameplay.mouseSensitivity),
      invertMouseY:source.gameplay?.invertMouseY===true,
      controllerAimSensitivity:clampPercent(source.gameplay?.controllerAimSensitivity,DEFAULT_GAME_SETTINGS.gameplay.controllerAimSensitivity),
      controllerAimAssist:source.gameplay?.controllerAimAssist!==false,
      controllerAimAssistStrength:clampPercent(
        source.gameplay?.controllerAimAssistStrength,
        DEFAULT_GAME_SETTINGS.gameplay.controllerAimAssistStrength,
      ),
    },
    graphics:normalizeGraphicsSettings(source.graphics),
    controls:Object.fromEntries(Object.entries(DEFAULT_GAME_SETTINGS.controls).map(([action,code])=>{
      let saved=typeof controls[action]==='string'&&controls[action]?controls[action]:code;
      return [action,saved];
    })),
    mouse:normalizeMouseSettings(source.mouse),
  };
}

function normalizeMouseButton(value,fallback){
  const number=Number(value);
  return Number.isInteger(number)&&number>=0&&number<=4?number:fallback;
}

function normalizeMouseSettings(value={}){
  const source=value&&typeof value==='object'?value:{};
  // `recenterButton` era el RMB del esquema anterior. Se reutiliza como el
  // botón que habilita temporalmente cámara + cañón horizontal.
  const result={
    fireButton:normalizeMouseButton(source.fireButton,DEFAULT_GAME_SETTINGS.mouse.fireButton),
    cameraReverseButton:normalizeMouseButton(
      source.cameraReverseButton,
      DEFAULT_GAME_SETTINGS.mouse.cameraReverseButton,
    ),
    cameraControlButton:normalizeMouseButton(
      source.cameraControlButton??source.recenterButton,
      DEFAULT_GAME_SETTINGS.mouse.cameraControlButton,
    ),
  };
  if(new Set(Object.values(result)).size!==3)return {...DEFAULT_GAME_SETTINGS.mouse};
  return result;
}

export function loadGameSettings(storage=localStorage){
  try{return normalizeGameSettings(JSON.parse(storage.getItem(GAME_SETTINGS_KEY)||'{}'));}
  catch{return normalizeGameSettings();}
}

export function saveGameSettings(settings,storage=localStorage){
  const normalized=normalizeGameSettings(settings);
  storage.setItem(GAME_SETTINGS_KEY,JSON.stringify(normalized));
  return normalized;
}

export function resetGameSettings(storage=localStorage){
  const defaults=normalizeGameSettings();
  storage.setItem(GAME_SETTINGS_KEY,JSON.stringify(defaults));
  return defaults;
}

const KEY_LABELS=Object.freeze({
  Space:'ESPACIO',ShiftLeft:'SHIFT IZQ.',ShiftRight:'SHIFT DER.',
  ControlLeft:'CTRL IZQ.',ControlRight:'CTRL DER.',AltLeft:'ALT IZQ.',AltRight:'ALT DER.',
  ArrowUp:'↑',ArrowDown:'↓',ArrowLeft:'←',ArrowRight:'→',
  Enter:'ENTER',Backspace:'RETROCESO',Tab:'TAB',CapsLock:'BLOQ. MAYÚS',
  Semicolon:';',Quote:"'",Comma:',',Period:'.',Slash:'/',Backslash:'\\',
  BracketLeft:'[',BracketRight:']',Minus:'-',Equal:'=',Backquote:'`',
});

export function keyCodeLabel(code){
  if(KEY_LABELS[code])return KEY_LABELS[code];
  if(/^Key[A-Z]$/.test(code))return code.slice(3);
  if(/^Digit\d$/.test(code))return code.slice(5);
  if(/^Numpad\d$/.test(code))return `NUM ${code.slice(6)}`;
  return String(code||'—').replace(/([a-z])([A-Z])/g,'$1 $2').toUpperCase();
}
